// Emailing an invoice with its PDF attached (#622): the Send Invoice
// drawer's words, its two templates filled for a recipient, its placeholder
// menu, who To may name, and the `messages` document a Send queues — or the
// one New Invoice's "Yes, email invoices immediately" has the function queue.
//
// Adapters over core/src/invoice_email.rs, which holds what MMS does and where
// we knowingly differ, and which both phones call too. Pure, and mirrored into functions/domain/: the sender fills a token
// the teacher left in the message with the same `invoiceEmailFill`, and the
// invoice function queues its emails with `invoiceEmailAutoDocument`.
import { coreSync } from './core.js';
import { documentFromRows } from './documents.js';
import { frozenBodyOf } from './documentRender.js';
import { studioFormatters } from './locale.js';
import { format } from './money.js';

const str = (v) => (v == null ? null : String(v));

const toRecipient = (r) => ({ name: str(r.name), address: String(r.address), channel: String(r.channel || 'email') });
const fromRecipient = (r) => ({ name: r.name ?? null, address: r.address, channel: r.channel });

const toContact = (c) => ({
  group: 0,
  id: str(c.id),
  firstName: str(c.firstName),
  lastName: str(c.lastName),
  email: str(c.email),
  mobile: str(c.mobile),
  mobileSmsCapable: Boolean(c.mobileSmsCapable),
});

/** A word of the drawer's, New Invoice's Email option's or the sender's. */
export const invoiceEmailWord = (code) => coreSync().invoiceEmailMessage(String(code)) ?? String(code);

/** Every code `invoiceEmailWord` answers from its own table. */
export const invoiceEmailWordCodes = () => [...coreSync().invoiceEmailMessageCodes()];

/** The drawer's tabs: `{ key, label, subject, body }`, Invoice first. */
export const invoiceEmailTemplates = () => [...coreSync().invoiceEmailTemplates()].map((t) => ({ ...t }));

/**
 * What the values are made from. `invoiceDate` and `invoiceAmount` are
 * already written in the studio's Region — the caller's formatters, which on
 * the web are the screen's and on the server `studioFormatters` of the same
 * stored locale (#500).
 */
export function invoiceEmailFacts({
  contact = null, familyLastName = null, invoiceDate = null, invoiceAmount = null, studioName = null, teacherName = null,
  teacherFirstName = null, teacherLastName = null,
} = {}) {
  return {
    firstName: str(contact?.firstName),
    lastName: str(contact?.lastName),
    title: str(contact?.title),
    familyLastName: str(familyLastName),
    invoiceDate: str(invoiceDate),
    invoiceAmount: str(invoiceAmount),
    studioName: str(studioName),
    teacherName: str(teacherName),
    // The stored pair (#797), used over a split of `teacherName` when either
    // is there (`values` in core/src/invoice_email.rs).
    teacherFirstName: str(teacherFirstName),
    teacherLastName: str(teacherLastName),
  };
}

/**
 * The invoice's date and Total Due as its email writes them: from its frozen
 * body (#622) — the date as the PDF prints it, the amount in the currency and
 * Region it was issued in — or, for an invoice from before invoices were
 * frozen, in the studio's Region now (`studioLocale`). One function, so the
 * drawer that fills the template and the sender that fills a token left in
 * it write the same figures.
 */
export function invoiceEmailFigures(invoice, studioLocale = null) {
  const total = Math.round(Number(invoice?.totalDueMinor) || 0);
  const body = frozenBodyOf(invoice);
  if (body) {
    return {
      invoiceDate: String(body.invoice.date || ''),
      invoiceAmount: format(total, body.studio.currency, body.studio.locale),
    };
  }
  const fmt = studioFormatters(studioLocale);
  const iso = invoice?.invoiceDate ? String(invoice.invoiceDate) : '';
  return { invoiceDate: (iso && fmt.formatDate(iso)) || iso, invoiceAmount: fmt.formatMoney(total) };
}

/** A tab's template filled for one recipient: `{ subject, body }`. */
export function invoiceEmailFilled(template, facts) {
  const r = coreSync().invoiceEmailFilled(str(template), facts);
  return { subject: r.subject, body: r.body };
}

/** `text` with each invoice token filled; `html` escapes each value. */
export const invoiceEmailFill = (text, facts, html = false) => coreSync().invoiceEmailFill(str(text) ?? '', facts, Boolean(html));

/** The keys of the invoice tokens `text` holds. */
export const invoiceEmailPlaceholdersUsed = (text) => [...coreSync().invoiceEmailPlaceholdersUsed(str(text) ?? '')];

/** The drawer's placeholder menu: `{ key, token, label, source }`. */
export const invoiceEmailPlaceholders = () => [...coreSync().invoiceEmailPlaceholders()].map((p) => ({ ...p }));

/**
 * A menu entry picked over `start`..`end` of `text`: `{ value, start, end,
 * refusal }`, `refusal` (`{ code, message }`) meaning nothing changed.
 */
export function invoiceEmailInsertPlaceholder(text, start, end, key, max) {
  const r = coreSync().invoiceEmailInsertPlaceholder(
    str(text) ?? '',
    Number.isInteger(start) ? start : undefined,
    Number.isInteger(end) ? end : undefined,
    String(key),
    max,
  );
  return {
    value: r.value, start: r.start, end: r.end, refusal: r.refusal ? { code: r.refusal.code, message: r.refusal.message } : null,
  };
}

/**
 * To's choices for a family: `{ choices: [{ contact, recipient }], at }` —
 * each of `contacts` (tab order) with an email address, beside its record,
 * and the position To opens on: the billing contact's (`billing`, the record
 * `familyBillingContact` in ./people.js answers) where it has an address,
 * else the first; null when nobody on the family can be emailed.
 */
export function invoiceEmailChoices(family, contacts = [], billing = null) {
  const list = (Array.isArray(contacts) ? contacts : []).filter(Boolean);
  const at = billing ? list.findIndex((c) => c === billing || (c.id != null && c.id === billing.id)) : -1;
  const r = coreSync().invoiceEmailChoices(
    family ? { id: str(family.id), lastName: str(family.lastName) } : undefined,
    list.map(toContact),
    at >= 0 ? at : undefined,
  );
  const choices = [...r.to].map((recipient, i) => ({ contact: list[r.contacts[i]], recipient: fromRecipient(recipient) }));
  return { choices, at: r.defaultIndex ?? null };
}

/** The id a Send from a drawer opened with `seed` queues under. */
export const invoiceEmailId = (seed) => coreSync().invoiceEmailId(String(seed));

/** The id New Invoice's email for `invoiceId` is queued under. */
export const invoiceEmailAutoId = (invoiceId) => coreSync().invoiceEmailAutoId(String(invoiceId));

/**
 * What a Send writes: `{ id, message, refusal }` — the message with its
 * `requestedAt` as `serverTimestamp`, and `refusal` the sentence when the
 * drawer would refuse it (and then nothing should be written).
 */
export function invoiceEmailWrite({
  seed, template = null, to = [], cc = [], subject = null, body = null, invoiceId = null, familyId = null,
  studentIds = [], sendAs = null, serverTimestamp = null,
} = {}) {
  const core = coreSync();
  const args = [
    String(seed), str(template), to.filter(Boolean).map(toRecipient), cc.filter(Boolean).map(toRecipient),
    str(subject), str(body), str(invoiceId), str(familyId),
    (Array.isArray(studentIds) ? studentIds : []).filter((id) => typeof id === 'string'),
    str(sendAs),
  ];
  const ref = core.invoiceEmail(...args);
  const refusal = core.invoiceEmailRefusal(to.length, cc.length, str(subject), str(body), str(invoiceId));
  const message = documentFromRows(core.invoiceEmailDocument(...args), serverTimestamp);
  return { id: ref.id, message, refusal: refusal ? refusal.message : null };
}

/**
 * The email New Invoice's function queues for an invoice it created: the
 * Invoice template's tokens, to `to`, under `invoiceEmailAutoId(invoiceId)`.
 */
export function invoiceEmailAutoDocument({
  invoiceId, familyId = null, to, studentIds = [], serverTimestamp = null,
}) {
  return documentFromRows(
    coreSync().invoiceEmailAutoDocument(String(invoiceId), str(familyId), toRecipient(to), studentIds.filter((id) => typeof id === 'string')),
    serverTimestamp,
  );
}

/** What a send whose PDF could not be made fails with. */
export const invoicePdfFailedText = (reason = null) => coreSync().invoiceEmailPdfFailedText(str(reason));

/** New Invoice step two's Email: `{ value, label }`, its default first. */
export const invoiceEmailOptions = () => [...coreSync().invoiceEmailOptions()].map((o) => ({ value: o.value, label: o.label }));
